import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import TeamPosition from "./TeamPosition";
import { League, Tournament } from "../WeekAtAGlance/IndividualTournament";
import { useNavigate } from "react-router-dom";
import IndividualPlayerCard from "./IndividualPlayerCard";

export interface TeamPositionPlayers {
  players: TeamPositionPlayer[];
  isSplitSpot: boolean;
  isTeamCaptain: boolean;
}

export interface TeamPositionPlayer {
  playerId: number | null;
  isVisitingPlayer: boolean;
  visitingPlayerName?: string;
  visitingPlayerRating?: number;
}

export interface AvailablePlayer {
  label: string;
  value: number;
}

export interface playerAPIInterface {
  player_id: number | null;
  position: number;
  is_split_spot: boolean;
  is_visiting_player: boolean;
  visiting_player_name?: string;
  visiting_player_handicap?: number;
}

function TeamSubmission({
  devPort,
  clubId,
  eventDetails,
  managerMode,
}: {
  devPort: string;
  clubId: number;
  eventDetails: Tournament;
  managerMode: number;
}) {
  function createDefaultTeamMembers() {
    const newTeamMembers: TeamPositionPlayers[] = [];
    for (let i = 0; i < 4; i++) {
      newTeamMembers.push({
        players: [
          {
            playerId: null,
            isVisitingPlayer: false,
            visitingPlayerName: "",
            visitingPlayerRating: 0,
          },
        ],
        isSplitSpot: false,
        isTeamCaptain: false,
      });
    }
    setTeamMembers(newTeamMembers);
  }

  const [availablePlayers, setAvailablePlayers] = useState<AvailablePlayer[]>(
    []
  );
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [teamName, setTeamName] = useState("");
  const [selectedLeague, setSelectedLeague] = useState<number | null>(null);
  const [unavailablePlayers, setUnavailablePlayers] = useState<number[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamPositionPlayers[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();

  const getUnavailablePlayers = useCallback(() => {
    const fetchTeams =
      devPort + "tournament_team_list/" + clubId + "/" + selectedLeague;
    console.log(fetchTeams);
    fetch(fetchTeams)
      .then((response) => response.json())
      .then((data) => {
        console.log("Unavailable Players");
        console.log(data);
        let unavailablePlayers: number[] = [];
        if (data.length > 0) {
          data.forEach((player: any) => {
            //If this is not a visiting player, add the id
            if (!player[7] && player[12] === 1) {
              unavailablePlayers.push(player[2]);
            }
          });
        }
        setUnavailablePlayers(unavailablePlayers);
      });
  }, [devPort, clubId, selectedLeague]);

  useEffect(() => {
    getUnavailablePlayers();
  }, [selectedLeague, getUnavailablePlayers]);
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await fetch(
          devPort + "player_list/" + clubId + "/False"
        );
        const data = await response.json();

        // Create a Map to store unique players by their ID
        const uniquePlayersMap = new Map();

        data.forEach((player: any[]) => {
          const playerId = player[4];
          // Only add if we haven't seen this player ID before
          if (!uniquePlayersMap.has(playerId)) {
            uniquePlayersMap.set(playerId, {
              label: `${player[0]} ${player[1]}`,
              value: playerId,
            });
          }
        });

        // Convert Map values back to array
        const formattedPlayers = Array.from(uniquePlayersMap.values());
        setAvailablePlayers(formattedPlayers);
        setLoadingPlayers(false);
      } catch (error) {
        console.error("Error fetching players:", error);
        setLoadingPlayers(false);
      }
    };

    fetchPlayers();
  }, [devPort, clubId]);

  // Reset team members when league changes
  useEffect(() => {
    createDefaultTeamMembers();
  }, [selectedLeague]);

  function resetForm() {
    setTeamName("");
    setSelectedLeague(null);
    createDefaultTeamMembers();
  }

  function submitTeam() {
    console.log(teamMembers);
    let errorMsg = "";
    let hasTeamCaptain = false;
    teamMembers.forEach((member, positionIndex) => {
      member.players.forEach((player, playerIndex) => {
        if (!player.playerId && !player.visitingPlayerName) {
          errorMsg +=
            "Missing a name for position " +
            (positionIndex + 1) +
            " and player " +
            (playerIndex + 1) +
            "\n";
        }
      });
      if (member.isTeamCaptain) {
        hasTeamCaptain = true;
      }
    });
    if (!hasTeamCaptain) {
      errorMsg += "Missing a team captain\n";
    }
    if (!teamName) {
      errorMsg += "Missing a team name\n";
    }
    if (!selectedLeague) {
      errorMsg += "Missing a league\n";
    }
    if (errorMsg) {
      alert(errorMsg);
    } else {
      console.log(teamMembers);
      const submitTeam = devPort + "add_tournament_team_with_players";
      let playerList: playerAPIInterface[] = [];
      teamMembers.map((member, index) =>
        member.players.map((player, playerIndex) =>
          playerList.push({
            player_id: player.playerId,
            position: index,
            is_split_spot: member.isSplitSpot,
            is_visiting_player: player.isVisitingPlayer,
            visiting_player_name: player.visitingPlayerName,
            visiting_player_handicap: player.visitingPlayerRating,
          })
        )
      );
      const teamData = {
        tournament_id: selectedLeague,
        team_name: teamName,
        total_handicap: 12,
        is_accepted: false,
        player_list: playerList,
      };
      console.log(teamData);
      fetch(submitTeam, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify(teamData),
      }).then((response) => {
        console.log(response);
        if (response.ok) {
          setShowSuccess(true);
          resetForm();
          if (managerMode !== -1) {
            navigate("/tournamentsignups");
          }
        }
      });
    }
  }

  return (
    <Card sx={{ margin: "auto" }}>
      <CardContent>
        <Typography variant="h5" component="div" gutterBottom>
          Make Your Team
        </Typography>
        <TextField
          fullWidth
          label="Team Name"
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="league-select-label">League</InputLabel>
          <Select
            labelId="league-select-label"
            id="league-select"
            value={selectedLeague}
            label="League"
            onChange={(e) => setSelectedLeague(Number(e.target.value))}
          >
            {eventDetails?.leagues?.map((league: League) => (
              <MenuItem key={league.tournament_id} value={league.tournament_id}>
                {league.league_name} ({league.handicap} handicap)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {!selectedLeague && (
          <Typography color="warning.main" sx={{ mb: 2 }}>
            Please select a league before adding players
          </Typography>
        )}
        {selectedLeague && managerMode === -1 && (
          <IndividualPlayerCard
            devPort={devPort}
            clubId={clubId}
            league_id={selectedLeague}
            managerMode={managerMode}
          />
        )}
        {selectedLeague && (
          <Box sx={{ mt: 2 }}>
            {teamMembers.map((member, index) => (
              <TeamPosition
                key={index}
                index={index}
                positionPlayers={member}
                teamMembers={teamMembers}
                setTeamMembers={setTeamMembers}
                availablePlayers={availablePlayers}
                loadingPlayers={loadingPlayers}
                unavailablePlayers={unavailablePlayers}
                isSinglePlayer={false}
                managerMode={managerMode}
              />
            ))}
          </Box>
        )}
        <Button
          variant="contained"
          color="primary"
          style={{ width: "100%" }}
          onClick={submitTeam}
          disabled={!selectedLeague}
        >
          Submit
        </Button>
      </CardContent>
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          Team has been successfully submitted!
        </Alert>
      </Snackbar>
    </Card>
  );
}

export default TeamSubmission;
